import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as RiskIcon
} from '@mui/icons-material';

import { useTranslation } from 'react-i18next';

import useRoadmapStore from '../store/roadmapStore';
import { STEP_LABELS } from '../utils/constants';
import PageContainer from '../components/PageContainer';
import StepPageHeader from '../components/StepPageHeader';
import useAISuggest from '../hooks/useAISuggest';
import apiService from '../utils/api';

const RiskCard = ({ risk, index, onEdit, onDelete }) => {
  const { t } = useTranslation();
  
  const getLikelihoodColor = (likelihood) => {
    switch (likelihood?.toLowerCase()) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getImpactColor = (impact) => {
    switch (impact?.toLowerCase()) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
          {risk.description || 'Untitled Risk'}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip 
            label={t('stepContent.risks.card.likelihood', { level: risk.likelihood || 'Medium' })}
            color={getLikelihoodColor(risk.likelihood)}
            size="small"
          />
          <Chip 
            label={t('stepContent.risks.card.impact', { level: risk.impact || 'Medium' })}
            color={getImpactColor(risk.impact)}
            size="small"
          />
        </Box>
        
        {risk.mitigation && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('stepContent.risks.form.fields.mitigation')}:
            </Typography>
            <Typography variant="body2">
              {risk.mitigation}
            </Typography>
          </Box>
        )}

        {risk.owner && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Owner: {risk.owner}
          </Typography>
        )}
      </CardContent>
      
      <CardActions>
        <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(index)}>
          {t('common.edit')}
        </Button>
        <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => onDelete(index)}>
          {t('common.delete')}
        </Button>
      </CardActions>
    </Card>
  );
};

const RiskForm = ({ risk, onSave, onCancel }) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    description: risk?.description || '',
    likelihood: risk?.likelihood || 'Medium',
    impact: risk?.impact || 'Medium',
    mitigation: risk?.mitigation || '',
    owner: risk?.owner || '',
    status: risk?.status || 'Open'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.description.trim()) {
      onSave(formData);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {risk ? t('stepContent.risks.form.editTitle') : t('stepContent.risks.form.addTitle')}
      </Typography>
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('stepContent.risks.form.fields.name')}
              value={formData.description}
              onChange={handleChange('description')}
              placeholder={t('stepContent.risks.form.placeholders.name')}
              required
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>{t('stepContent.risks.form.fields.likelihood')}</InputLabel>
              <Select
                value={formData.likelihood}
                onChange={handleChange('likelihood')}
                label={t('stepContent.risks.form.fields.likelihood')}
              >
                <MenuItem value="Low">{t('common.low')}</MenuItem>
                <MenuItem value="Medium">{t('common.medium')}</MenuItem>
                <MenuItem value="High">{t('common.high')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>{t('stepContent.risks.form.fields.impact')}</InputLabel>
              <Select
                value={formData.impact}
                onChange={handleChange('impact')}
                label={t('stepContent.risks.form.fields.impact')}
              >
                <MenuItem value="Low">{t('common.low')}</MenuItem>
                <MenuItem value="Medium">{t('common.medium')}</MenuItem>
                <MenuItem value="High">{t('common.high')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={handleChange('status')}
                label="Status"
              >
                <MenuItem value="Open">Open</MenuItem>
                <MenuItem value="Mitigated">Mitigated</MenuItem>
                <MenuItem value="Closed">Closed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('stepContent.risks.form.fields.mitigation')}
              multiline
              rows={3}
              value={formData.mitigation}
              onChange={handleChange('mitigation')}
              placeholder={t('stepContent.risks.form.placeholders.mitigation')}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('stepContent.risks.form.fields.owner')}
              value={formData.owner}
              onChange={handleChange('owner')}
              placeholder={t('stepContent.risks.form.placeholders.owner')}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}
              sx={{ '& > .MuiButton-root': { width: { xs: '100%', sm: 'auto' } } }}>
              <Button
                type="submit"
                variant="contained"
                disabled={!formData.description.trim()}
              >
                {risk ? t('common.update') : t('stepContent.risks.actions.addRisk')}
              </Button>
              <Button onClick={onCancel}>
                {t('common.cancel')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default function RisksPage() {
  const { 
    getStepData, 
    updateStepData, 
    markStepComplete, 
    isStepCompleted 
  } = useRoadmapStore();
  const { t, i18n } = useTranslation();
  const handleAISuggest = useAISuggest('risks');
  const _storeStep = useRoadmapStore(s => s.roadmapData?.steps?.risks);
  const { roadmapData } = useRoadmapStore();

  const stepData = getStepData('risks');
  const [risks, setRisks] = useState(stepData.risk_items || []);
  const [isComplete, setIsComplete] = useState(isStepCompleted('risks'));
  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [riskScanLoading, setRiskScanLoading] = useState(false);

  const handleRiskScan = async () => {
    setRiskScanLoading(true);
    try {
      const desc = roadmapData.steps?.vision?.vision_statement || roadmapData.project_name || '';
      const result = await apiService.riskScan(desc, '', risks.map(r => r.description), i18n.language);
      if (result.risk_items?.length) {
        const newRisks = [...risks, ...result.risk_items.map(r => ({
          description: r.description,
          likelihood: r.likelihood || 'medium',
          impact: r.impact || 'medium',
          mitigation: r.mitigation || '',
          owner: r.owner || ''
        }))];
        updateData(newRisks);
      }
    } catch { /* handled silently */ }
    finally { setRiskScanLoading(false); }
  };

  useEffect(() => {
    const data = getStepData('risks');
    setRisks(data.risk_items || []);
    setIsComplete(isStepCompleted('risks'));
  }, [getStepData, isStepCompleted, _storeStep]);

  const updateData = (newRisks) => {
    setRisks(newRisks);
    updateStepData('risks', {
      risk_items: newRisks
    });
  };

  const handleSaveRisk = (riskData) => {
    if (editingIndex !== null) {
      const updated = risks.map((risk, index) => 
        index === editingIndex ? riskData : risk
      );
      updateData(updated);
    } else {
      updateData([...risks, riskData]);
    }
    
    setShowForm(false);
    setEditingIndex(null);
  };

  const handleEditRisk = (index) => {
    setEditingIndex(index);
    setShowForm(true);
  };

  const handleDeleteRisk = (index) => {
    if (window.confirm(t('common.confirmDelete'))) {
      const updated = risks.filter((_, i) => i !== index);
      updateData(updated);
    }
  };

  const handleCompleteToggle = () => {
    const newCompleteState = !isComplete;
    const success = markStepComplete('risks', newCompleteState);
    setIsComplete(success);
  };

  const canComplete = risks.length > 0;

  const getRisksByCategory = () => {
    const highRisks = risks.filter(r => r.likelihood === 'High' || r.impact === 'High');
    const mediumRisks = risks.filter(r => (r.likelihood === 'Medium' || r.impact === 'Medium') && !highRisks.includes(r));
    const lowRisks = risks.filter(r => r.likelihood === 'Low' && r.impact === 'Low');
    
    return { highRisks, mediumRisks, lowRisks };
  };

  const { highRisks, mediumRisks, lowRisks } = getRisksByCategory();

  return (
    <PageContainer maxWidth={1200}>
      <StepPageHeader
        stepNum={7}
        title={t(STEP_LABELS.risks)}
        intro={t('stepContent.risks.intro')}
        isComplete={isComplete}
        canComplete={canComplete}
        onToggleComplete={handleCompleteToggle}
        requireMessage={t('stepContent.risks.alerts.requireRisks')}
      onAISuggest={handleAISuggest}
        />

      {/* Risk Scan Button */}
      <Stack direction="row" spacing={1} sx={{ mb: -1 }}>
        <Button
          size="small"
          onClick={handleRiskScan}
          disabled={riskScanLoading}
          startIcon={riskScanLoading ? <CircularProgress size={14} /> : <RiskIcon />}
          sx={{
            border: '1px solid rgba(245,158,11,0.3)',
            color: 'var(--status-amber)',
            background: 'rgba(245,158,11,0.08)',
            '&:hover': { borderColor: '#f59e0b', background: 'rgba(245,158,11,0.18)' },
            fontWeight: 600, fontSize: '0.78rem', textTransform: 'none', px: 2
          }}
        >
          {riskScanLoading ? t('ai.scanning') : t('ai.riskScan')}
        </Button>
        <Typography variant="caption" sx={{ color: 'var(--fg-faint)', alignSelf: 'center' }}>
          {t('ai.poweredByGrok')}
        </Typography>
      </Stack>

      <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
        {risks.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={4}>
              <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4">{highRisks.length}</Typography>
                  <Typography variant="body2">High Risk</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4}>
              <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4">{mediumRisks.length}</Typography>
                  <Typography variant="body2">Medium Risk</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4}>
              <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4">{lowRisks.length}</Typography>
                  <Typography variant="body2">Low Risk</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingIndex(null);
            setShowForm(true);
          }}
          sx={{ mb: 3 }}
        >
          {t('stepContent.risks.actions.addRisk')}
        </Button>
      </Paper>

      {/* Risk Form */}
      {showForm && (
        <Box sx={{ mb: 3 }}>
          <RiskForm
            risk={editingIndex !== null ? risks[editingIndex] : null}
            onSave={handleSaveRisk}
            onCancel={() => {
              setShowForm(false);
              setEditingIndex(null);
            }}
          />
        </Box>
      )}

      {/* Risks List */}
      {risks.length > 0 && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RiskIcon />
            Risk Register ({risks.length})
          </Typography>
          
          <Grid container spacing={3}>
            {risks.map((risk, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <RiskCard
                  risk={risk}
                  index={index}
                  onEdit={handleEditRisk}
                  onDelete={handleDeleteRisk}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {risks.length === 0 && !showForm && (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('stepContent.risks.empty.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('stepContent.risks.empty.description')}
          </Typography>
        </Paper>
      )}
    </PageContainer>
  );
}